<template>
    <div class="w-full h-full">
        <FormModal v-if="showFormModal" @cancel="showFormModal = false" @confirm="submit()"
            :title="selectedRetailer.retailerName" subtitle="Select users who spend at this retailer."
            :valid="addRetailerRule.condition != undefined && addRetailerRule.amount != undefined"
            submitButtonText="Add rule">
            <div>
                <p class="text-xs font-semibold text-gray-500 pb-1">Example:</p>
                <div class="rounded-lg py-3 px-3 bg-gray-50 border border-gray-300 text-sm">
                    <p class="leading-6">Select users that have spent <span
                            class="border bg-white rounded px-1.5 p-1">CONDITION</span> <span class="text-nowrap">£
                            <span class="border bg-white rounded px-1.5 p-1">AMOUNT</span></span> at {{
                                selectedRetailer.retailerName }}
                    </p>
                </div>
            </div>
            <div class="border-t pt-3 grid grid-cols-1 gap-1">
                <p class="text-xs font-semibold text-gray-600">Enter your variables:</p>
                <CustomSelect v-model="addRetailerRule.condition" label="Condition" pre-select="MORE_THAN" class=""
                    :options="rewardType" />
                <CustomInput v-model="addRetailerRule.amount" label="Amount (£)" placeholder="100" class=""
                    type="number" />
                <p v-if="addRetailerRule.condition != undefined && addRetailerRule.amount != undefined"
                    class="text-xs font-semibold text-gray-600 pt-3 mb-0">Your retailer rule:</p>
                <div v-if="addRetailerRule.condition != undefined && addRetailerRule.amount != undefined"
                    class="rounded-lg py-3 px-3 bg-gray-50 border border-gray-300 text-sm">
                    <p class="leading-6">Select users that have spent <span
                            class="border bg-white rounded px-1.5 p-1">{{ getConditionText(addRetailerRule.condition)
                            }}</span> <span class="text-nowrap">£ <span class="border bg-white rounded px-1.5 p-1">{{
                                addRetailerRule.amount }}</span></span> at {{ selectedRetailer.retailerName }}
                    </p>
                </div>
            </div>


        </FormModal>
        <Header title="Create a new audience" icon="Plus" />

        <WhiteBox title="Name & timeframe">
            <template v-slot:content>
                <div class="-mt-2 rounded col-span-full grid grid-cols-4 gap-4">
                    <CustomInput v-model="audienceObject.name" class="col-span-full xl:col-span-2" label="Audience name"
                        placeholder="Describe this audience" />
                    <CustomSelect v-model="audienceObject.period" :pre-select=30 class="col-span-full xl:col-span-2"
                        label="Select a rolling analysis timeframe:" :options="periodSelect" />
                </div>
            </template>
        </WhiteBox>


        <WhiteBox title="Segment by overall spend volume:">
            <template v-slot:buttons>
                <CustomButton id="createContract" small text="Add rule" icon="Plus" type="navigate"
                    @click="audienceObject.demographicConditions.push({ ...addDemographicData })" />

            </template>
            <template v-slot:content>
                <div class="grid gap-y-2">
                    <div v-for="val, index in audienceObject.demographicConditions" :key="index"
                        class="bg-gray-50 via-white to-white border border-gray-300 rounded-lg px-3 pt-2 pb-2">
                        <div class="flex justify-between items-center">
                            <div>
                                <p class="text-gray-700 font-semibold text-sm">
                                    Rule {{ index + 1 }}
                                </p>
                            </div>
                            <div>
                                <CustomButton id="createContract" small icon="Trash2" type="warning"
                                    @click="audienceObject.demographicConditions.splice(index, 1)" />
                            </div>
                        </div>
                        <div class="sm:flex items-center sm:-mt-2 text-sm text-gray-800">
                            <p class="">Select users that have spent:</p>
                            <div class="flex items-center min-w-[246px]">
                                <CustomSelect v-model="audienceObject.demographicConditions[index].condition"
                                    pre-select="MORE_THAN" class="ml-2 mb-1" :options="rewardType" />
                                <p class="ml-2">£</p>
                                <CustomInput v-model="audienceObject.demographicConditions[index].amount"
                                    placeholder="100" class="ml-2 max-w-[100px] mb-1" type="number"></CustomInput>
                            </div>
                        </div>
                    </div>
                </div>

            </template>
        </WhiteBox>

        <WhiteBox title="Segment by retailer:">
            <template v-slot:buttons>
                <CustomButton id="createContract" small v-if="audienceObject.retailerSegments.length == 0"
                    text="Add retailer rules" icon="Plus" type="navigate"
                    @click="audienceObject.retailerSegments.push(addRetailerSegment)" />
                <CustomButton id="createContract" small v-if="audienceObject.retailerSegments.length > 0" text="Remove"
                    icon="Trash2" type="warning" @click="audienceObject.retailerSegments.pop()" />
            </template>
            <template v-slot:content>
                <div v-for="retailerSegment, index in audienceObject.retailerSegments" :key="index">
                    <div class="mt-6">
                        <SearchBar placeholderText="Search by retailer name"
                            @search="(val) => { getRetailerBySearch(0, 50, val); showResultsTable = true }">
                            <CustomButton small text="" type="default" icon="X"
                                @click="retailers = []; showResultsTable = false;" />
                        </SearchBar>
                    </div>
                    <EasyDataTable class="-mt-2" v-if="showResultsTable" :headers="headersTable1" :items="retailers"
                        :loading="loading" v-model:server-options="serverOptions"
                        :server-items-length="serverItemsLength" :hide-footer="true" :no-hover="true"
                        :table-min-height="false">
                        <!-- Loading State -->
                        <template #loading>
                            <div class="p-4 w-full flex justify-center">
                                <svg class="animate-spin text-gray-400 w-12 h-12" xmlns="http://www.w3.org/2000/svg"
                                    fill="none" viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                        stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                                    </path>
                                </svg>
                            </div>
                        </template>

                        <!-- Empty State -->
                        <template #empty-message>
                            <div class="flex justify-center py-4">
                                <div
                                    class="flex justify-center items-center text-gray-500 rounded-full bg-gray-50 px-5 py-2">
                                    <p class="text-sm mr-2">No results</p>
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                        stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                                        <path stroke-linecap="round" stroke-linejoin="round"
                                            d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0 1 12 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 1.5v-1.5m0 0c0-.621.504-1.125 1.125-1.125m0 0h7.5" />
                                    </svg>
                                </div>
                            </div>
                        </template>
                        <template #header-action="header">
                            <div class="w-full text-right">
                                {{ header.text }}
                            </div>
                        </template>
                        <!-- Retailer Name -->
                        <template #item-retailer="retailers">

                            <LogoAndText class="text-xs" :logo="retailers.logo"
                                :text="retailers.retailerName" />
                        </template>

                        <!-- Retailer Website -->
                        <template #item-website="retailers">
                            {{ retailers.retailerWebsite }}
                        </template>

                        <!-- Action Buttons -->
                        <template #item-action="retailers">
                            <div class="flex justify-end items-center py-1 gap-1">
                                <CustomButton small text="Add retailer" type="navigate" icon="Plus" @click="
                                    showFormModal = true;
                                selectedRetailer = retailers;
                                addRetailerRule.retailerWebsite = retailers.retailerWebsite;
                                addRetailerRule.retailerName = retailers.retailerName;
                                addRetailerRule.logo = retailers.logo;
                                " />
                            </div>
                        </template>
                    </EasyDataTable>

                    <div v-if="audienceObject.retailerSegments.length > 0" class="mt-4">
                        <p v-if="audienceObject.retailerSegments.length > 0 && audienceObject.retailerSegments[0].retailerConditions.length > 0"
                            class="text-gray-700 font-semibold text-sm mb-1">
                            Retailer rules:
                        </p>
                        <div v-for="(rule, index) in audienceObject.retailerSegments[0].retailerConditions" :key="index"
                            class="bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 mb-2">
                            <div class="flex justify-between items-center">
                                <LogoAndText class="text-sm" :logo="rule.logo" :text="rule.retailerName" />
                                <CustomButton id="removeRetailerRule" small icon="Trash2" type="warning"
                                    @click="audienceObject.retailerSegments[0].retailerConditions.splice(index, 1)" />
                            </div>
                            <div class="text-sm text-gray-800 my-1">
                                <div>
                                    Select users that have spent
                                    <span class="font-semibold">{{ getConditionText(rule.condition) }} </span>
                                    <span class="font-semibold"> £{{ rule.amount }}</span> at
                                    <span class="font-semibold">{{ rule.retailerName }} </span>
                                    ({{ rule.retailerWebsite }})
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </WhiteBox>

        <CustomButton :loading="this.loading" :disabled="!validForm || !validateDemographics || !validateRetailers"
            class="w-full shadow-xl transform hover:scale-[1.01] transition duration-200" id="createContract"
            text="Create audience" type="action" icon="WandSparkles" @click="createAudience()" />

    </div>
</template>

<script setup>
</script>

<script>
import Header from "@/components/Header.vue";
import WhiteBox from "@/components/WhiteBox.vue";
import CustomInput from "@/components/inputs/CustomInput.vue";
import CustomButton from "@/components/inputs/CustomButton.vue";
import FormModal from "@/components/modals/FormModal.vue";
import { DateTime } from 'luxon';
import CustomSelect from "@/components/inputs/CustomSelect.vue";
import SearchBar from '@/components/SearchBar.vue'
import LogoAndText from "@/components/LogoAndText.vue";


export default {
    name: "manage-publsihers",
    components: {
        Header,
        WhiteBox,
        CustomInput,
        CustomButton,
        FormModal,
        CustomSelect,
        LogoAndText,
        SearchBar
    },
    props: {
    },

    data() {
        return {
            serverOptions: {
                page: 1,
                rowsPerPage: 50,
            },

            selectedRetailer: undefined,
            showResultsTable: false,

            rewardType: [
                { "id": "MORE_THAN", "text": "more than" },
                { "id": "LESS_THAN", "text": "less than" },
            ],

            periodSelect:
                [
                    { "id": 30, "text": "30 days" },
                    { "id": 60, "text": "60 days" },
                    { "id": 90, "text": "90 days" },

                ],

            addDemographicData:
            {
                condition: "MORE_THAN",
                amount: undefined,
            },

            addRetailerRule: {
                retailerWebsite: "",
                retailerName: "",
                logo: "",
                condition: "MORE_THAN",
                amount: undefined,
            },

            addRetailerSegment: {
                operand: "AND",
                retailerConditions: [
                ]
            },

            audienceObject: {
                name: "",
                period: 30,
                demographicConditions: [

                ],
                retailerSegments: []
            },

            retailerSearch: [],
            categorySOW: [],
            serverItemsLength: undefined,
            startDate: '',
            endDate: '',
            formStart: '',
            formEnd: '',
            category: "",

            showErrorModal: false,
            showSuccessModal: false,
            showFormModal: false,
            loading: false,
            headersTable1: [
                { text: "Retailer", value: "retailer" },
                { text: "Website", value: "website" },
                { text: "Action", value: "action" },
            ],

            headersTable2: [
                { text: "Retailer", value: "retailerWebsite" },
                { text: "Condition", value: "condition" },
                { text: "Amount", value: "amount" },
                { text: "Rolling number of days", value: "period" },
                { text: "Action", value: "action" },
            ],
            retailers: [

            ],
        }
    },
    created() {
        const now = DateTime.local();
        const startOfMonth = now.startOf('month');
        const endOfMonth = now.endOf('month');
        this.startDate = startOfMonth.minus({ month: 2 });
        this.endDate = endOfMonth;
        this.formStart = this.startDate.toFormat('dd-MM-yyyy')
        this.formEnd = this.endDate.toFormat('dd-MM-yyyy')

    },
    methods: {

        submit() {
            // Push a copy of the rule to avoid reference issues
            this.audienceObject.retailerSegments[0].retailerConditions.push({ ...this.addRetailerRule });
            this.showFormModal = false;
            this.retailers = [];
            this.showResultsTable = false;
            this.selectedRetailer = undefined;
            // Reset addRetailerRule
            this.addRetailerRule = {
                retailerWebsite: "",
                retailerName: "",
                logo: "",
                condition: "MORE_THAN",
                amount: undefined,
            };
        },

        niceDateTime(dateObject) {
            return DateTime.fromISO(dateObject).toLocaleString(DateTime.DATE_SHORT)
        },

        updateAnalytics() {
            this.startDate = DateTime.fromISO(this.formStart).startOf('day');
            this.endDate = DateTime.fromISO(this.formEnd).endOf('day');
            this.formStart = this.startDate.toFormat('dd-MM-yyyy')
            this.formEnd = this.endDate.toFormat('dd-MM-yyyy')
            this.getRetailers(0, 50)
            this.getCategories()


        },

        async createAudience() {
            try {
                const token = localStorage.getItem('token')
                const api = localStorage.getItem('apiType')


                var body = { ...this.audienceObject }

                if (this.audienceObject.retailerSegments.length > 0) {
                    body.retailerSegments[0].retailerConditions.forEach(element => {
                        delete element.retailerName;
                        delete element.logo;

                    });
                }

                const response = await this.$apiFetch(`https://${api}.boogi.io/private/api/client/audience`, {
                    method: "POST",
                    body: JSON.stringify(body),
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `${token}`,
                    },
                });

                if (response.ok) {
                    this.$router.push('/audience')
                }

            } catch (error) {
                console.error(error)
                return;
            }
        },

        async getRetailerBySearch(page, size, search) {
            try {
                const token = localStorage.getItem('token')
                const api = localStorage.getItem('apiType')

                this.loading = true

                const response = await this.$apiFetch(`https://${api}.boogi.io/private/api/client/retailer/search?page=${page}&size=${size}&search=${search}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `${token}`,
                    },
                });

                if (response.ok) {
                    var object = await response.json()
                    this.serverItemsLength = object.length
                    this.retailers = object.content
                    this.loading = false
                }

            } catch (error) {
                return;
            }

        },
        getConditionText(condition) {
            const conditionMap = {
                MORE_THAN: "more than",
                LESS_THAN: "less than"
            };
            return conditionMap[condition] || condition;
        },

    },
    computed: {
    validForm(){
            return this.audienceObject.name != "" && (this.audienceObject.demographicConditions.length > 0 || this.audienceObject.retailerSegments.length > 0)
    },
    validateDemographics(){
        if(this.audienceObject.demographicConditions.length > 0)
        {
            for(var i=0;i<this.audienceObject.demographicConditions.length;i++)
            {
                var element = this.audienceObject.demographicConditions[i]
                if(element.amount == undefined)
                {
                    return false
                }

            }

        }
        return true

    },
    validateRetailers(){
        if(this.audienceObject.retailerSegments.length > 0)
        {
            if(this.audienceObject.retailerSegments[0].retailerConditions.length == 0){
                return false
            }

            for(var i=0;i<this.audienceObject.retailerSegments[0].retailerConditions.length;i++)
            {
                var element = this.audienceObject.retailerSegments[0].retailerConditions[i]
                if(element.retailerWebsite != "" && element.amount == undefined)
                {
                    return false
                }

            }

        }
        return true
    }

    },
    watch: {
        serverOptions: {
            deep: true,
            handler(val) {
                this.getRetailers(val.page - 1, val.rowsPerPage)
            }
        }
    }
}
</script>